<script>
   /**************************************************************/
   /* PCVCalculator                                              */
   /* -------------------------                                  */
   /* component for computing a PV-set                           */
   /**************************************************************/

   import { Select, Container, Button, Spinner, ButtonCancel, ButtonDownload } from 'svelte-controls-basic';
   import { untrack } from 'svelte';
   import { pcvpls } from '@mdatools-apps/wasm/pls.js';
   import { downloadData, app_pls_predict, relabelObjects } from '@mdatools-apps/shared/utils';

   // main properties of the whole app
   let {
      WASM,
      model,
      calset,
      calres,
      outliers = [],
      relabeled = [],
      pvset = $bindable(),
      pvres = $bindable(),
      pvscalars = $bindable(),
      ncompSelected = $bindable(),
   } = $props();

   // prefix for the label ids the controls below are named by. it must be unique on the page and
   // every tab of the app stays mounted, so it can not be written out by hand
   const uid = $props.id();

   // error handling parameters
   let status = $state('neutral');
   let errMsg = $state('');

   // promise container for calculator
   let promise = $state();

   // every asynchronous write is stamped with the run which asked for it, and only the newest
   // run may write; a run is retired by a newer one and by the component going away. the C call
   // itself blocks the page, so the window is the module instantiation, which yields: a cancel
   // of the model pressed in it unmounts the component, and the callback still ran and computed
   // the pv-set; that it then threw on the cleared calibration results, before writing through
   // the bindings, was luck, not a guard. plain 'let', never '$state' (see 'PrepWidget')
   let run = 0;
   $effect(() => () => { run++; });

   // compute number of segments options based on dataset size. computed once, from what the
   // component is mounted with, and untrack marks that as deliberate: the tab mounts this
   // component under its model, and the handler which changes the outliers clears the model in
   // the same call, so no render sees one changed without the other
   const nsegOptions = untrack(() => {
      const n = calset.values.nrows - outliers.length;
      return calset.values.nrows > 0 ?
         [3, 4, 5, 10, 20].filter(v => v >= (n / (n - model.ncomp - 1)) && v <= n / 2) : [];
   });

   // PV-set calculation parameters — if no segment options, limit to LOO
   let cvtypeOptions = $state(nsegOptions.length === 0 ? ['loo'] : ['ven', 'rand', 'loo']);
   let cvType = $state(nsegOptions.length === 0 ? 'loo' : 'ven');
   let nseg = $state(nsegOptions.length > 0 ? nsegOptions[0] : 0);

   // function that computes PV-set using given parameters
   async function computePVSet(calset, model, cv, outliers) {
      const myRun = ++run;
      promise = WASM()
         .then(Module => {
            if (myRun !== run) return;

            // the relabelling sits inside the chain: 'compute' does not await this function, so
            // a throw out here would be an unhandled rejection with 'promise' never assigned and
            // no message shown
            const values = relabelObjects(calset.values.copy(), relabeled, calset.nresp);

            const cv_type = cv.type == 'loo' ? 0 : cv.type == 'rand' ? 2 : 1;
            const [pvvalues, scalars] = pcvpls(Module, model, values, calset.nresp, model.ncomp, cv.nseg, cv_type, 0, outliers);
            pvset = {

               classLabelsVarname: calset.classLabelsVarname,
               classNames: calset.classNames,
               // the class of each kept row, read off the dummy 'pcvpls' copies over:
               // 'calset.classes' has every row, and the class before relabelling
               classes: Array.from(pvvalues.getcolref(1), v => calset.classNames[v > 0 ? 0 : 1]),
               dec: calset.dec,
               fileName: calset.fileName + '-pcv',
               hasClassNames: calset.hasClassNames,
               hasLabels: calset.hasLabels,
               header: calset.header,
               labelsVarname: calset.labelsVarname,

               objindices: calres.objindices,
               objlabels: calres.objlabels,
               sep: calset.sep,
               values: pvvalues,
               varAxisTitle: calset.varAxisTitle,

               varindices: model.varindices,
               varlabels: model.varlabels,
               varrev: model.varrev,
               varvalues: model.varvalues,

               varvaluesName: model.varvaluesName,
               varvaluesUnits: model.varvaluesUnits,
               nresp: model.nresp
            }
            // pv-set is created for outliers free data, hence outliers is empty here
            pvres = app_pls_predict(Module, model, pvset, []);
            pvscalars = scalars;
            // set here, where the results are made, and not in an effect of the tab: an effect
            // would also run when a restored session mounts, and overwrite the stored number
            ncompSelected = getOptimalNComp(model, pvres);
            status = 'neutral';
         })
         .catch((e) => {
            console.error(e)
            if (myRun !== run) return;
            pvset = null;
            pvres = null;
            pvscalars = null;
            status = 'error';
            // the message only: the box printed the Error object, which reads 'Error: ...'
            errMsg = e instanceof Error ? e.message : String(e);
         });
   }

   // the optimal number of components: how many come before the first one whose Q2 gain over
   // the previous is at or below the threshold, all of them if there is none
   function getOptimalNComp(model, pvres) {
      const rmse = pvres.stat.rmse.getcolref(1);
      const Q2 = rmse.map((v, i) => i > 0 ? 1 - v*v / (rmse[i-1]**2) : 1);
      const ncomp = Q2.findIndex(v => v <= 0.0975);
      return ncomp < 0 ? model.ncomp : ncomp;
   }

   // event handler which triggers the PV-set computation. the controls are hidden while the run
   // is in flight, as in the shared 'PCVCalculator': every call of 'WASM' instantiates the module
   // anew, and a second click in that time started a second run
   function compute() {
      status = 'computing';
      errMsg = '';
      computePVSet(calset, model, {type: cvType, nseg: nseg}, outliers);
   }

   // function which resets the state of the component
   function reset() {
      status = 'neutral';
      errMsg = '';
      promise = null;
      pvset = null;
      pvres = null;
      pvscalars = null;
   }
</script>


{#if model && !pvset && status !== 'computing'}
   <Container name="split" label='Split type:' id="{uid}-cvtype">
      <Select bind:value={cvType} options={cvtypeOptions} />
   </Container>

   {#if cvType !== 'loo'}
   <Container name="nseg" label='Segments:' id="{uid}-nseg">
      <Select bind:value={nseg} options={nsegOptions} />
   </Container>
   {/if}

   <Container name="run-button" label=' '>
      <Button onclick={compute} text="Compute PV-set" />
   </Container>
{/if}

{#if promise}

   <!-- the chain above ends in a catch of its own which reports through 'errMsg', so the promise
        never rejects and a catch block here would never show -->
   {#await promise}
      <Container name="spinner">
         <Spinner />
         <p>Computing PV-set...</p>
      </Container>
   {/await}

{/if}

{#if pvset }
<Container name="info">
   <p>
      <span>PV-set:</span>
      {cvType}
      {cvType !== 'loo' ? nseg + ' seg' : ''}<br>
      <span>Size:</span>
      {pvset.values.nrows}&times;{pvset.values.ncols - pvset.nresp} <span>(X)</span>,
      {pvset.values.nrows}&times;{pvset.nresp} <span>(Y)</span>,
   </p>
   <ButtonDownload onclick={() => downloadData(pvset, calset.fileName + '-pv')} />
   <ButtonCancel onclick={reset} />
</Container>
{/if}

{#if status === 'error' && errMsg}
   <Container name="error" status='error'>
      <p>{errMsg}</p>
   </Container>
{/if}

<style>
   p {
      line-height: 1.5em;
   }

   span {
      color:#a0a0a0;
   }
</style>
